'use client';
import { useRouter } from '@bprogress/next';
import { ProcessStep } from '@c/process/BaseProcess';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStripe } from '@stripe/react-stripe-js';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { productCacheTag } from '@util/cache';
import { db } from '@util/firebase';
import { setUnmatchedBrand } from '@util/firestore/brands/brands.service';
import { getCategoryByID } from '@util/firestore/categories';
import {
  createListingFormToProductDocument,
  logSmartListingEvent,
  SMART_LISTING_VERSION,
  SmartListingEventArgs,
  validateProduct,
} from '@util/firestore/listing';
import { createPayment } from '@util/firestore/payments';
import {
  addProductDoc,
  addProductDraftDoc,
  addProductToDataDepot,
  getCategoryByProductCats,
  getProductByIdNotClean,
  getProductId,
  updateProductDraftDoc,
  updateProductListingDoc,
} from '@util/firestore/products';
import useDebounce from '@util/hooks/useDebounce';
import { revalidatePath } from '@util/index';
import { logError } from '@util/logError';
import { getProductSlug, slugify } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { CategoryDocument } from 'models/category';
import {
  ProductDocument,
  productSchema,
  Variation,
  variationSchemaPreTransform,
} from 'models/product';
import { usePathname, useSearchParams } from 'next/navigation';
import { parseAsInteger, useQueryState } from 'nuqs';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { z } from 'zod';
import { VEHICLE_CATEGORIES } from 'models/product';
import { BIKE_PACKAGE_PRICES } from '../_components/BikePackage';
import CreateListingSearch from '../_components/CreateListingSearch';
import CreateListingStep from '../_components/CreateListingStep';
import FindVehicle from '../_components/FindVehicle';
import SelectListingCategory from '../_components/SelectListingCategory';
import SelectListingSport from '../_components/SelectListingSport';
import SelectListingType from '../_components/SelectListingType';

type CreateListingContextType = {
  form: UseFormReturn<ProductDocument>;
  product: ProductDocument;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  goToStep: (id: StepId) => void;
  variant: 'create-listing' | 'edit-listing';
  steps: ProcessStep<StepId>[];
  onSubmit: SubmitHandler<ProductDocument>;
  onSubmitError: SubmitErrorHandler<ProductDocument>;
  saveDraft: () => void;
  submitting: boolean;
  unknownError: string;
  categoryInfo: CategoryDocument[];
  setCategoryInfo: Dispatch<SetStateAction<CategoryDocument[]>>;
  selectedPaymentMethodId: string | null;
  setSelectedPaymentMethodId: Dispatch<SetStateAction<string>>;
  isOtherBrand: boolean;
  setIsOtherBrand: Dispatch<SetStateAction<boolean>>;
  logSmartListingEventMutation: UseMutationResult<
    void,
    Error,
    SmartListingEventArgs
  >;
  debouncedSearchTerm: string;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  searchId: string;
  sourceVariations: Variation[] | null;
  setSourceVariations: Dispatch<SetStateAction<Variation[] | null>>;
};
export type StepId =
  | 'select-listing-type'
  | 'find-vehicle'
  | 'search'
  | 'select-listing-sport'
  | 'select-listing-category'
  | 'create-listing';

/**
 * Canonical step order — an index in this array IS the numeric `currentStep`
 * value. `goToStep` resolves a `StepId` to its index here, so navigation never
 * hardcodes magic numbers. `find-vehicle` is a branch off `select-listing-type`
 * (reached only from the "Vehicle for Sale" card), not part of the linear gear
 * path; its position here only fixes its `currentStep` index.
 */
const STEP_ORDER: StepId[] = [
  'select-listing-type',
  'search',
  'select-listing-sport',
  'select-listing-category',
  'create-listing',
  'find-vehicle',
];

const CreateListingContext = createContext({} as CreateListingContextType);

// For a smart listing, re-derive the persisted MPN from the data depot
// variation matching the listing's final size/color. Runs at save time so the
// MPN reflects the chosen variation even if the seller changed the size after
// the initial selection.
function applySmartListingMpn(
  product: ProductDocument,
  sourceVariations: Variation[] | null
) {
  if (!sourceVariations?.length || product.has_variations) return;
  const letter = product.size?.letter;
  const number = product.size?.number;
  if (letter == null && number == null) return;
  const matched = sourceVariations.find((v) => {
    const sizeMatches =
      letter != null ? v.size === letter : v.size === number;
    return sizeMatches && (!product.color || v.color === product.color);
  });
  if (matched?.mpn) product.mpn = matched.mpn;
}

export const CreateListingProvider = ({
  children,
  initialStep = 'select-listing-type',
}: {
  children: ReactNode;
  initialStep?: StepId;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const session_id = useMemo(() => window.crypto.randomUUID(), []);
  const searchId = useMemo(
    () => window.crypto.randomUUID(),
    [debouncedSearchTerm]
  );
  const logSmartListingEventMutation = useMutation({
    mutationFn: (args: SmartListingEventArgs) => {
      return logSmartListingEvent({ ...args, session_id });
    },
    onError: (error) => {
      logError(error);
    },
  });
  const [unknownError, setUnknownError] = useState('');
  const pathname = usePathname();
  const variant = pathname?.split('/').at(1) as
    | 'create-listing'
    | 'edit-listing';
  const params = useSearchParams();

  const [fetchedCats, setFetchedCats] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState<CategoryDocument[]>([]);

  const router = useRouter();
  const productId = useMemo(() => getProductId(), []);

  const [isOtherBrand, setIsOtherBrand] = useState(false);

  // Variations of the data depot product a smart listing was created from.
  // Kept in memory (not persisted) so step 3 can re-derive the MPN when the
  // seller changes the size/color of a non-variation listing.
  const [sourceVariations, setSourceVariations] = useState<Variation[] | null>(
    null
  );

  const defaultShipping = [
    {
      code: 'US',
      cost: 0,
      name: 'Continental US',
      enabled: true,
    },
  ];
  const { userDoc } = useAuth();
  const form = useForm<ProductDocument>({
    mode: 'onSubmit',
    resolver: zodResolver(
      productSchema.extend({
        variations: z
          .array(
            variationSchemaPreTransform.omit({ price: true }).extend({
              price: z.union([z.number(), z.string()]),
            })
          )
          .optional(),
      })
    ),
    defaultValues: async () => {
      const id =
        variant === 'edit-listing'
          ? pathname?.split('/').at(2)
          : params?.get('id');
      const purchasePrice = params?.get('purchase_price');
      let initialProduct = id ? await getProductByIdNotClean(id) : undefined;
      return {
        ...(initialProduct ?? {}),
        favorite_count:
          variant === 'edit-listing' ? initialProduct!.favorite_count : 0,
        id: variant === 'edit-listing' ? initialProduct!.id : productId,
        brand: initialProduct?.brand ?? '',
        ...(initialProduct?.bike_package && {
          bike_package: initialProduct.bike_package,
        }),
        mpn: params?.get('return_id') ? [] : initialProduct?.mpn ?? [],
        sku: params?.get('return_id') ? '' : initialProduct?.sku ?? '',
        shipping_costs: initialProduct?.shipping_costs ?? defaultShipping,
        shipment: params?.get('return_id')
          ? initialProduct?.shipment
            ? {
                ...initialProduct.shipment,
                ship_from: userDoc?.addresses?.[0] ?? undefined,
              }
            : null
          : initialProduct?.shipment ?? null,
        size: initialProduct?.size,
        date_updated: Date.now(),
        date_added:
          variant === 'edit-listing'
            ? initialProduct?.date_added ?? Date.now()
            : Date.now(),
        created:
          variant === 'edit-listing'
            ? initialProduct?.created ?? Date.now()
            : Date.now(),
        gender: initialProduct?.gender ?? 'U',
        is_flat_rate: initialProduct?.is_flat_rate ?? false,
        location: initialProduct?.location,
        location_display: initialProduct?.location_display,
        curated_lists:
          variant === 'edit-listing' ? initialProduct?.curated_lists : [],
        category: initialProduct?.category ?? '',
        category1: initialProduct?.category1 ?? null,
        category2: initialProduct?.category2 ?? null,
        category3: initialProduct?.category3 ?? null,
        condition: initialProduct?.condition ?? undefined,
        stock: params?.get('return_id')
          ? 1
          : variant === 'edit-listing'
          ? initialProduct?.stock ?? 1
          : Math.max(initialProduct?.stock ?? 0, 1),
        slug: initialProduct?.slug ?? '',
        country_code: initialProduct?.country_code ?? 'US',
        categories: initialProduct?.categories ?? [],
        price:
          variant === 'create-listing'
            ? initialProduct?.og_price ?? 1
            : initialProduct?.price ?? 1,
        og_price: initialProduct?.og_price ?? 1,
        purchase_entry_price: purchasePrice
          ? Number(purchasePrice)
          : initialProduct?.purchase_entry_price ?? 0,
        purchase_entry_id: purchasePrice
          ? 'MXSC000323'
          : initialProduct?.purchase_entry_id ?? undefined,
        on_sale:
          variant === 'create-listing'
            ? false
            : initialProduct?.on_sale ?? false,
        out_of_stock:
          variant === 'create-listing'
            ? false
            : initialProduct?.out_of_stock ?? false,
        seller_id: initialProduct?.seller_id ?? userDoc?.uid ?? '',
        account_id: initialProduct?.account_id ?? userDoc?.account_id ?? '',
        thumbnail: '',
        fitting_bikes: initialProduct?.fitting_bikes ?? {
          universal: false,
          bikes: [],
        },
        from_web: initialProduct?.from_web ?? true,
        bike_package_ch:
          variant === 'edit-listing'
            ? initialProduct?.bike_package_ch
            : undefined,
        seller_type:
          initialProduct?.seller_type ?? userDoc?.seller_type ?? 'individual',
        images: initialProduct?.images ?? [],
        ride_types: initialProduct?.ride_types ?? [],
        variations: params?.get('return_id')
          ? []
          : initialProduct?.variations ?? [],
        has_variations: params?.get('return_id')
          ? false
          : initialProduct?.has_variations ?? false,
        integration_info:
          params?.get('return_id') || variant === 'create-listing'
            ? undefined
            : initialProduct?.integration_info,
        specs: params?.get('return_id') ? undefined : initialProduct?.specs,
        sold_date: undefined,
        bought_by: [],
        sold_count: undefined,
        group_id: params?.get('return_id')
          ? undefined
          : initialProduct?.group_id,
        custom_inputs: params?.get('return_id')
          ? undefined
          : initialProduct?.custom_inputs,
      } as ProductDocument;
    },
  });
  const product = form.watch();
  const initialStepIndex = STEP_ORDER.indexOf(initialStep);
  const [currentStep, setCurrentStep] = useQueryState('step', {
    history: 'replace',
    ...parseAsInteger.withDefault(initialStepIndex),
    defaultValue: initialStepIndex,
  });

  const goToStep = (id: StepId) => {
    setCurrentStep(STEP_ORDER.indexOf(id));
  };

  const [chargingForBike, setChargingForBike] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const stripe = useStripe();

  useEffect(() => {
    if (!categoryInfo.length && !fetchedCats && product.category) {
      setFetchedCats(true);
      getCategoryByProductCats(product).then((cat: CategoryDocument | null) => {
        const rideTypeId = cat?.id.split('/').slice(0, 2).join('/');
        const mainCatId = cat?.id.split('/').slice(0, 4).join('/');
        const cat1Id = cat?.id.split('/').slice(0, 6).join('/');
        const cat2Id = cat?.id.split('/').slice(0, 8).join('/');
        const cat3Id = cat?.id.split('/').slice(0, 10).join('/');
        const cat4Id = cat?.id.split('/').slice(0, 12).join('/');
        Promise.all(
          [rideTypeId, mainCatId, cat1Id, cat2Id, cat3Id, cat4Id].map((id) =>
            id ? getCategoryByID(id) : null
          )
        ).then((cats) =>
          setCategoryInfo(
            cats
              .filter(Boolean)
              .filter(
                (cat, index) =>
                  cats.findIndex((c) => c?.id === cat?.id) === index
              ) as CategoryDocument[]
          )
        );
      });
    }
  }, [categoryInfo.length, product.category, variant, fetchedCats, product]);

  const createDraftMutation = useMutation({
    mutationFn: addProductDraftDoc,
    onSuccess: async (_void, data) => {
      const path = getProductSlug(data as ProductDocument);
      const product = data as ProductDocument;
      await revalidatePath(path, undefined, [
        productCacheTag(product.id),
        'product:list',
      ]);
      router.push(path);
    },
    onError: (error) => {
      logError('Create Listing Unknown Error' + error.message);
      setUnknownError(error.message);
    },
  });

  const updateDraftMutation = useMutation({
    mutationFn: updateProductDraftDoc,
    onSuccess: async (_void, data) => {
      const path = getProductSlug(data as ProductDocument);
      const product = data as ProductDocument;
      await revalidatePath(path, undefined, [
        productCacheTag(product.id),
        'product:list',
      ]);
      router.push(path);
    },
    onError: (error) => {
      logError('Create Listing Unknown Error' + error.message);
      setUnknownError(error.message);
    },
  });

  const createProductMutation = useMutation({
    mutationFn: addProductDoc,
    onSuccess: async (_void, data) => {
      const path = getProductSlug(data as ProductDocument);
      const product = data as ProductDocument;
      await revalidatePath(path, undefined, [
        productCacheTag(product.id),
        'product:list',
        'homepage',
      ]);
      logSmartListingEventMutation.mutate({
        event: 'listing_published',
        session_id: '',
        event_id: window.crypto.randomUUID(),
        search_id: searchId,
        uid: userDoc?.uid ?? '',
        device: window.innerWidth > 768 ? 'desktop' : 'mobile',
        smart_listing_version: SMART_LISTING_VERSION,
        listing_id: data.id,
        sources: data.sources ?? [],
      });
      router.push(path + '?share=true');
    },
    onError: (error) => {
      logError('Create Listing Unknown Error' + error.message);
      setUnknownError(error.message);
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: updateProductListingDoc,
    onSuccess: async (_void, data) => {
      const path = getProductSlug(data as ProductDocument);
      const product = data as ProductDocument;
      await revalidatePath(path, undefined, [
        productCacheTag(product.id),
        'product:list',
      ]);
      router.push(path);
    },
    onError: (error) => {
      logError('Create Listing Unknown Error' + error.message);
      setUnknownError(error.message);
    },
  });

  const addToDataDepotMutation = useMutation({
    mutationFn: addProductToDataDepot,
    onSuccess: async (_void, data) => {
      const sku = data.sku;
      if (sku) {
        const path = `/product/${slugify(data.title)}/${sku}`;
        router.push(path);
      } else {
        alert('No sku found for product');
      }
    },
    onError: (error) => {
      alert('Error. Open the console to see the error.');
      logError(
        'Error adding product to data depot ' + (error as Error).message
      );
    },
  });

  async function chargeSellerForBikePackage(product: ProductDocument) {
    if (!userDoc) return;
    if (
      product.bike_package &&
      product.bike_package > 1 &&
      !product.bike_package_ch &&
      VEHICLE_CATEGORIES.includes(product.category)
    ) {
      if (!selectedPaymentMethodId) {
        form.setError('bike_package_ch', {
          type: 'required',
          message: 'Missing payment method',
        });
        throw new Error('Missing payment method');
      }
      const price = BIKE_PACKAGE_PRICES[product.bike_package] ?? 0;
      const { data } = await createPayment({
        amount: price,
        // @ts-ignore - hacking this backend function to work for bike packages
        order: {
          product_ids: [product.id],
          customer_id: userDoc.customer_id ?? '',
        },
        metadata: {
          seller_id: userDoc.uid,
          product_id: product.id,
        },
      });
      const res = await stripe?.confirmCardPayment(data, {
        payment_method: selectedPaymentMethodId,
      });
      if (!res) {
        logError(
          new Error(
            'Error charing for bike package' + ': ' + JSON.stringify(product)
          )
        );
        form.setError('bike_package_ch', {
          type: 'required',
          message: 'Error charging for bike package',
        });
        throw new Error('Missing payment method');
      }
      if (res.error) {
        logError(
          new Error('Error charing for bike package' + ': ' + res.error.message)
        );
        form.setError('bike_package_ch', {
          type: 'required',
          message: 'Error charging for bike package',
        });
        throw new Error('Missing payment method');
      }
      product.bike_package_ch = res?.paymentIntent?.id;
    }
  }
  useEffect(() => {
    const isVehicle = VEHICLE_CATEGORIES.includes(product.category);
    if ((product.is_flat_rate || isVehicle) && product.shipment) {
      form.setValue('shipment', null);
    } else if (
      !product.is_flat_rate &&
      !product.shipment &&
      !isVehicle &&
      userDoc?.addresses.length
    ) {
      form.setValue('shipment', {
        ship_from: userDoc?.addresses[0],
        package: {
          dimensions: { length: 0, width: 0, height: 0, unit: 'inch' },
          weight: { value: 0, unit: 'ounce' },
        },
      });
    }
  }, [
    product.is_flat_rate,
    form,
    product.shipment,
    userDoc?.addresses,
    product.category,
  ]);
  if (!product.id) return null;

  const steps: ProcessStep<StepId>[] = [
    {
      id: 'select-listing-type',
      content: <SelectListingType />,
      title: 'What are you selling?',
    },
    {
      id: 'search',
      content: <CreateListingSearch />,
      title: 'Search for a product',
    },
    {
      id: 'select-listing-sport',
      icon: <span className="text-brand-secondary">2</span>,
      content: <SelectListingSport />,
      title: 'Select a sport',
    },
    {
      id: 'select-listing-category',
      icon: <span className="text-brand-secondary">3</span>,
      content: <SelectListingCategory />,
      title: 'Select the categories',
    },
    {
      id: 'create-listing',
      icon: <span className="text-brand-secondary">4</span>,
      content: <CreateListingStep />,
      title: 'Create a listing',
    },
    {
      id: 'find-vehicle',
      content: <FindVehicle />,
      title: 'Find your vehicle',
    },
  ];

  async function saveDraft() {
    if (!userDoc || !variant || !product) return;

    const finalProduct = createListingFormToProductDocument(
      product,
      userDoc,
      variant === 'edit-listing' ? 'edit' : 'create',
      productId
    );
    applySmartListingMpn(finalProduct, sourceVariations);

    finalProduct.is_draft = true;
    finalProduct.out_of_stock = true;

    try {
      validateProduct(finalProduct, form.setError, undefined, undefined, {
        isDraft: true,
      });
    } catch (e) {
      if (e instanceof Error) {
        logError(
          e,
          JSON.stringify({
            product: finalProduct,
            form: form.formState.errors,
            formState: form.watch(),
          })
        );
      }
      return;
    }

    if (variant === 'edit-listing') {
      updateDraftMutation.mutate(finalProduct);
    } else {
      createDraftMutation.mutate(finalProduct);
    }
  }

  const onSubmit: SubmitHandler<ProductDocument> = async (data) => {
    if (!userDoc || !variant || !product) return;

    try {
      const finalProduct = createListingFormToProductDocument(
        data,
        userDoc,
        variant === 'create-listing' ? 'create' : 'edit',
        product.id
      );
      applySmartListingMpn(finalProduct, sourceVariations);
      // charge seller for bike listing
      try {
        validateProduct(
          finalProduct,
          form.setError,
          variant,
          categoryInfo[categoryInfo.length - 1]
        );
      } catch (e) {
        if (e instanceof Error) {
          logError(
            e,
            JSON.stringify({
              product: finalProduct,
              form: form.formState.errors,
              formState: form.watch(),
            })
          );
        }
        return;
      }
      if (finalProduct.bike_package && finalProduct.bike_package > 1) {
        try {
          setChargingForBike(true);
          await chargeSellerForBikePackage(finalProduct);
        } catch (e) {
          setChargingForBike(false);
          return;
        } finally {
          setChargingForBike(false);
        }
        // Guard against any path where the charge function returns without
        // throwing AND without setting `bike_package_ch` (e.g. inner gate
        // skipped silently). Without this, the listing would publish on a
        // paid tier with no payment captured.
        if (!finalProduct.bike_package_ch) {
          form.setError('bike_package_ch', {
            type: 'required',
            message:
              'Payment failed — your listing package could not be charged. Please try a different payment method.',
          });
          return;
        }
      }

      if (isOtherBrand) {
        await setUnmatchedBrand(product.brand);
      }

      finalProduct.is_draft = false;
      if (variant === 'edit-listing') {
        updateProductMutation.mutate(finalProduct);
      } else {
        createProductMutation.mutate(finalProduct);
        const returnId = params?.get('return_id');
        if (returnId) {
          try {
            await updateDoc(doc(db, 'returns', returnId), {
              relisted_product_id: finalProduct.id,
            });
          } catch (e) {
            logError(
              new Error('Error updating return doc' + ': ' + JSON.stringify(e))
            );
          }
        }
      }
    } catch (e) {
      setChargingForBike(false);
      logError(
        new Error('Error creating product doc' + ': ' + JSON.stringify(form))
      );
    }
  };
  const onSubmitError: SubmitErrorHandler<ProductDocument> = (errors) => {
    const errorID = Math.random().toString(36).slice(0, 10);
    logError(
      new Error(
        `CreateListing Unknown error (${errorID}): ${Object.entries(errors)
          .map(
            ([key, value]) => `${key}: ${(errors as any)[key as any].message}`
          )
          .join(', ')}`
      )
    );
    const fields = Object.keys(errors);
    if (fields.length)
      return setUnknownError(
        `The following fields are invalid: ${fields.join(
          ', '
        )}. Please correct them or contact support with the following error ID: ${errorID}`
      );
    return setUnknownError(
      'There are invalid fields. Please correct them or contact support with the following error ID: ' +
        errorID
    );
  };

  const submitting =
    updateProductMutation.isPending ||
    createProductMutation.isPending ||
    updateDraftMutation.isPending ||
    createDraftMutation.isPending ||
    addToDataDepotMutation.isPending ||
    chargingForBike;

  return (
    <CreateListingContext.Provider
      value={{
        form,
        currentStep,
        setCurrentStep,
        goToStep,
        product,
        variant,
        steps,
        onSubmit,
        onSubmitError,
        saveDraft,
        submitting,
        unknownError,
        categoryInfo,
        setCategoryInfo,
        selectedPaymentMethodId,
        setSelectedPaymentMethodId,
        isOtherBrand,
        setIsOtherBrand,
        logSmartListingEventMutation,
        debouncedSearchTerm,
        searchTerm,
        setSearchTerm,
        searchId,
        sourceVariations,
        setSourceVariations,
      }}
    >
      {children}
    </CreateListingContext.Provider>
  );
};

export const useCreateListing = (): CreateListingContextType => {
  const context = useContext(CreateListingContext);
  if (context === undefined) {
    throw new Error(
      'useCreateListing must be used within a CreateListingProvider'
    );
  }
  return context;
};
